/* eslint-disable no-script-url */
/* eslint-disable jsx-a11y/anchor-is-valid */
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useContext } from 'react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Dropdown, Label, Stack, TextField } from '@bingads-webui-react/fabric-control';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { ErrorString, SpanWithComponent, checkShouldShowError } from '@bingads-webui-campaign-react/wizard-shared-components';
import { audienceAdsOptionKeys, audienceAdsOptions, getAudiencesAdsErrorMsg, validateAudienceAds, getAudienceData } from './utility';

const getStyles = () => ({
  audienceAdsContainer: {
  },
});

function getValue(audienceAds) {
  const data = getAudienceData(audienceAds);
  return data.value;
}

const BidAdjustment = React.memo((props) => {
  const {
    audienceAds,
    setAudienceAds,
  } = props;
  const currentValue = getValue(audienceAds);
  const readOnly = audienceAds.useParentLevel;

  return (
    <div className="bid-adjustment">
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
        <TextField
          value={currentValue}
          placeholder=""
          ariaLabel="bid-adjustment-label"
          styles={{ fieldGroup: { width: 80 } }}
          onBlur={() => { setAudienceAds({ isDirty: true }); }}
          onChange={(e, value) => { setAudienceAds({ value, isDirty: true }); }}
          disabled={readOnly}
        />
        <span className="percent-mark">%</span>
      </Stack>
    </div>
  );
});

BidAdjustment.propTypes = {
  audienceAds: PropTypes.shape({}),
  setAudienceAds: PropTypes.func.isRequired,
};

BidAdjustment.defaultProps = {
  audienceAds: {},
};

function getSelectedKey(audienceAds) {
  const data = audienceAds.useParentLevel ? audienceAds.parentLevelSettings : audienceAds.currentLevelSettings;
  return data.option || audienceAdsOptionKeys.Increase;
}

export const AudienceAds = React.memo((props) => {
  const {
    audienceAds,
    setAudienceAds,
  } = props;

  const mySetAudienceAds = useCallback((partialUpdate) => {
    setAudienceAds({
      ...audienceAds,
      currentLevelSettings: {
        ...audienceAds.currentLevelSettings,
        ...partialUpdate,
      },
      isDirty: partialUpdate.isDirty || audienceAds.isDirty,
    });
  }, [audienceAds, setAudienceAds]);

  const classes = useDefaultStyles(getStyles);

  const i18n = useContext(I18nContext);
  const audienceAdsBidAdjustmentText = i18n.getString(_TL_('Audience ads bid adjustment'));
  const labelId = 'audience-ads-label';
  const shouldShowError = checkShouldShowError(audienceAds);
  const error = getAudiencesAdsErrorMsg({ code: validateAudienceAds(audienceAds), i18n });
  const selectedKey = getSelectedKey(audienceAds);
  const readOnly = audienceAds.useParentLevel;

  return (
    <div className={classnames(classes.audienceAdsContainer, 'audience-ads')}>
      <Label className="audience-ads" id={labelId} >
        <SpanWithComponent
          SpanText={audienceAdsBidAdjustmentText}
          Component={() => (
            <HelpPopup
              topic="pop_BA_NativeAd_BidAdj"
              aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: audienceAdsBidAdjustmentText })}
            />)}
        />
      </Label>
      <div className="audience-ads-options">
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
          <Dropdown
            aria-label="audience-ads-options-label"
            options={audienceAdsOptions(i18n)}
            defaultSelectedKey={selectedKey}
            styles={{
              dropdown: {
                width: 133,
              },
            }}
            disabled={readOnly}
            onChange={(e, option) => mySetAudienceAds({ option: option.key })}
          />
          <BidAdjustment {...props} setAudienceAds={mySetAudienceAds} />
        </Stack>
      </div>
      {shouldShowError && error && <ErrorString error={error} />}
    </div>
  );
});

AudienceAds.propTypes = {
  audienceAds: PropTypes.shape({}),
  setAudienceAds: PropTypes.func.isRequired,
};

AudienceAds.defaultProps = {
  audienceAds: {},
};
